import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  maxDisplay?: number;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  className,
  disabled = false,
  maxDisplay = 3,
}: MultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOptions = options.filter(opt => value.includes(opt.value));

  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const handleKeyboardAction = (
    e: React.KeyboardEvent<HTMLSpanElement>,
    action: () => void,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      action();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between h-auto min-h-[2.25rem] px-3 py-2',
            !selectedOptions.length && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1 flex-1 items-center">
            {selectedOptions.length === 0 ? (
              <span>{placeholder}</span>
            ) : selectedOptions.length <= maxDisplay ? (
              selectedOptions.map(opt => (
                <Badge
                  key={opt.value}
                  variant="gray"
                  className="mr-1 text-xs"
                >
                  {opt.label}
                  <span
                    role="button"
                    tabIndex={0}
                    className="ml-1 rounded-full hover:bg-accent/20"
                    onClick={(e) => handleRemove(opt.value, e)}
                    onKeyDown={(e) => handleKeyboardAction(e, () => onChange(value.filter(v => v !== opt.value)))}
                    aria-label={t('common.remove')}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))
            ) : (
              <>
                {selectedOptions.slice(0, maxDisplay).map(opt => (
                  <Badge
                    key={opt.value}
                    variant="gray"
                    className="mr-1 text-xs"
                  >
                    {opt.label}
                    <span
                      role="button"
                      tabIndex={0}
                      className="ml-1 rounded-full hover:bg-accent/20"
                      onClick={(e) => handleRemove(opt.value, e)}
                      onKeyDown={(e) => handleKeyboardAction(e, () => onChange(value.filter(v => v !== opt.value)))}
                      aria-label={t('common.remove')}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </Badge>
                ))}
                <Badge variant="gray" className="text-xs">
                  +{selectedOptions.length - maxDisplay}
                </Badge>
              </>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2">
            {selectedOptions.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                className="rounded-full hover:bg-accent/20 p-0.5"
                onKeyDown={(e) => handleKeyboardAction(e, () => onChange([]))}
                aria-label={t('common.clear')}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="p-2 border-b border-border">
          <Input
            ref={searchInputRef}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t('common.noResults')}
            </div>
          ) : (
            <div className="p-1">
              {filteredOptions.map(option => {
                const isSelected = value.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={cn(
                      'flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent/10',
                      isSelected && 'bg-accent/5'
                    )}
                    onClick={() => handleToggle(option.value)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(option.value)}
                    />
                    <span className="flex-1">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-accent" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
